import {
  autocompletion,
  type CompletionContext,
  type CompletionResult,
  type Completion,
} from '@codemirror/autocomplete';
import { syntaxTree } from '@codemirror/language';
import type { Extension } from '@codemirror/state';
import type { SyntaxNode } from '@lezer/common';
import type { ActiveFile } from '../types';

type CompletionItem = {
  label: string;
  type: 'property' | 'value';
  detail?: string;
  info?: string;
};

/**
 * Workflow JSON structure completions organized by context path.
 */
const workflowCompletions: Record<string, CompletionItem[]> = {
  root: [
    { label: 'definition', type: 'property', detail: 'Workflow definition', info: 'The main workflow definition object' },
    { label: 'kind', type: 'property', detail: 'Workflow kind', info: 'Stateful or Stateless' },
    { label: 'metadata', type: 'property', detail: 'Workflow metadata' },
  ],
  definition: [
    { label: '$schema', type: 'property', detail: 'Schema URL' },
    { label: 'contentVersion', type: 'property', detail: 'Version string' },
    { label: 'triggers', type: 'property', detail: 'Workflow triggers', info: 'Define how the workflow starts' },
    { label: 'actions', type: 'property', detail: 'Workflow actions', info: 'Define the workflow steps' },
    { label: 'parameters', type: 'property', detail: 'Workflow parameters' },
    { label: 'outputs', type: 'property', detail: 'Workflow outputs' },
  ],
  trigger: [
    { label: 'type', type: 'property', detail: 'Trigger type' },
    { label: 'kind', type: 'property', detail: 'Trigger kind' },
    { label: 'inputs', type: 'property', detail: 'Trigger inputs' },
    { label: 'position', type: 'property', detail: 'Canvas position' },
    { label: 'metadata', type: 'property', detail: 'Trigger metadata' },
  ],
  action: [
    { label: 'type', type: 'property', detail: 'Action type' },
    { label: 'kind', type: 'property', detail: 'Action kind' },
    { label: 'inputs', type: 'property', detail: 'Action inputs' },
    { label: 'runAfter', type: 'property', detail: 'Dependencies', info: 'Actions that must complete first' },
    { label: 'position', type: 'property', detail: 'Canvas position' },
    { label: 'trackedProperties', type: 'property', detail: 'Tracked properties' },
  ],
  actionType: [
    { label: 'Http', type: 'value', detail: 'HTTP request' },
    { label: 'Response', type: 'value', detail: 'HTTP response' },
    { label: 'If', type: 'value', detail: 'Conditional branch' },
    { label: 'Switch', type: 'value', detail: 'Multi-branch switch' },
    { label: 'Foreach', type: 'value', detail: 'Loop over array' },
    { label: 'Until', type: 'value', detail: 'Loop until condition' },
    { label: 'Scope', type: 'value', detail: 'Group actions' },
    { label: 'InitializeVariable', type: 'value', detail: 'Create variable' },
    { label: 'SetVariable', type: 'value', detail: 'Update variable' },
    { label: 'AppendToArrayVariable', type: 'value', detail: 'Append to array' },
    { label: 'IncrementVariable', type: 'value', detail: 'Increment number' },
    { label: 'DecrementVariable', type: 'value', detail: 'Decrement number' },
    { label: 'Compose', type: 'value', detail: 'Compose data' },
    { label: 'Parse', type: 'value', detail: 'Parse JSON' },
    { label: 'Query', type: 'value', detail: 'Filter array' },
    { label: 'Select', type: 'value', detail: 'Transform array' },
    { label: 'Join', type: 'value', detail: 'Join array to string' },
    { label: 'Table', type: 'value', detail: 'Create HTML/CSV table' },
    { label: 'Terminate', type: 'value', detail: 'End workflow' },
    { label: 'Wait', type: 'value', detail: 'Delay execution' },
  ],
  triggerType: [
    { label: 'Request', type: 'value', detail: 'HTTP request trigger' },
    { label: 'Recurrence', type: 'value', detail: 'Scheduled trigger' },
    { label: 'ApiConnection', type: 'value', detail: 'Connector trigger' },
    { label: 'ApiConnectionWebhook', type: 'value', detail: 'Webhook trigger' },
  ],
  triggerKind: [
    { label: 'Http', type: 'value', detail: 'HTTP endpoint' },
  ],
  position: [
    { label: 'x', type: 'property', detail: 'X coordinate' },
    { label: 'y', type: 'property', detail: 'Y coordinate' },
  ],
  workflowKind: [
    { label: 'Stateful', type: 'value', detail: 'Maintains run history' },
    { label: 'Stateless', type: 'value', detail: 'No run history, faster' },
  ],
};

/**
 * Connections JSON structure completions.
 */
const connectionsCompletions: Record<string, CompletionItem[]> = {
  root: [
    { label: 'managedApiConnections', type: 'property', detail: 'Managed API connections' },
    { label: 'serviceProviderConnections', type: 'property', detail: 'Service provider connections' },
  ],
};

/**
 * Creates a CodeMirror autocomplete extension for workflow/connections JSON.
 */
export const createJsonAutocomplete = (activeFile: ActiveFile): Extension => {
  return autocompletion({
    override: [
      (context: CompletionContext): CompletionResult | null => {
        const completions = activeFile === 'workflow.json' ? workflowCompletions : connectionsCompletions;

        // Get the word before cursor
        const word = context.matchBefore(/[\w"$]*$/);
        if (!word && !context.explicit) return null;

        // Analyze the current position in the JSON structure
        const tree = syntaxTree(context.state);
        const jsonContext = getJsonContext(tree, context.pos, context.state.doc.toString());

        // Get appropriate completions based on context
        const items = getCompletionsForContext(jsonContext, completions, activeFile);
        if (items.length === 0) return null;

        // Convert to CodeMirror completion format
        const options: Completion[] = items.map((item) => {
          const completion: Completion = {
            label: item.type === 'property' ? `"${item.label}"` : `"${item.label}"`,
            type: item.type,
            apply: item.type === 'property' ? `"${item.label}": ` : `"${item.label}"`,
            boost: item.type === 'property' ? 1 : 0,
          };
          if (item.detail) {
            completion.detail = item.detail;
          }
          if (item.info) {
            completion.info = item.info;
          }
          return completion;
        });

        return {
          from: word?.from ?? context.pos,
          options,
          validFor: /^["'\w$]*$/,
        };
      },
    ],
    icons: false,
  });
};

type JsonContext = {
  path: string[];
  isInPropertyName: boolean;
  isInPropertyValue: boolean;
  parentPropertyName: string | null;
};

/**
 * Analyzes the JSON syntax tree to determine the current editing context.
 */
const getJsonContext = (
  tree: ReturnType<typeof syntaxTree>,
  pos: number,
  doc: string
): JsonContext => {
  const path: string[] = [];
  let isInPropertyName = false;
  let isInPropertyValue = false;
  let parentPropertyName: string | null = null;

  // Walk up the tree from current position
  let node: SyntaxNode | null = tree.resolveInner(pos, -1);

  while (node) {
    const nodeType = node.type.name;

    if (nodeType === 'PropertyName') {
      isInPropertyName = true;
      // Extract the property name being edited
      const nameText = doc.slice(node.from, node.to).replace(/"/g, '');
      if (path.length === 0) {
        parentPropertyName = nameText;
      }
    } else if (nodeType === 'Property') {
      // Get the property name from this property node
      const nameNode = node.getChild('PropertyName');
      if (nameNode) {
        const name = doc.slice(nameNode.from, nameNode.to).replace(/"/g, '');
        path.unshift(name);
      }

      // Check if we're in the value part of this property
      const colonPos = doc.indexOf(':', node.from);
      if (colonPos !== -1 && pos > colonPos) {
        isInPropertyValue = true;
      }
    } else if (nodeType === 'String' || nodeType === 'Number' || nodeType === 'True' || nodeType === 'False' || nodeType === 'Null') {
      // We're in a value
      isInPropertyValue = true;
    }

    node = node.parent;
  }

  return { path, isInPropertyName, isInPropertyValue, parentPropertyName };
};

/**
 * Selects appropriate completions based on the JSON context.
 */
const getCompletionsForContext = (
  context: JsonContext,
  completions: Record<string, CompletionItem[]>,
  activeFile: ActiveFile
): CompletionItem[] => {
  const { path, isInPropertyValue, parentPropertyName } = context;

  // If we're editing a property value, suggest appropriate values
  if (isInPropertyValue && parentPropertyName) {
    if (parentPropertyName === 'type') {
      // Determine if this is a trigger or action type
      if (path.includes('triggers')) {
        return completions.triggerType ?? [];
      }
      return completions.actionType ?? [];
    }
    if (parentPropertyName === 'kind') {
      if (path.includes('triggers')) {
        return completions.triggerKind ?? [];
      }
      if (path.length === 1 && activeFile === 'workflow.json') {
        return completions.workflowKind ?? [];
      }
    }
  }

  // Suggest property names based on current path
  if (path.length === 0) {
    return completions.root ?? [];
  }

  // Match path patterns to completion groups
  const lastSegment = path[path.length - 1];

  if (path.includes('definition')) {
    if (lastSegment === 'definition') {
      return completions.definition ?? [];
    }
    if (path.includes('triggers') && path.length > path.indexOf('triggers') + 1) {
      return completions.trigger ?? [];
    }
    if (path.includes('actions') && path.length > path.indexOf('actions') + 1) {
      return completions.action ?? [];
    }
    if (lastSegment === 'triggers' || lastSegment === 'actions') {
      // Inside triggers/actions object, suggesting new trigger/action name
      return [];
    }
    if (lastSegment === 'position') {
      return completions.position ?? [];
    }
  }

  if (lastSegment === 'position') {
    return completions.position ?? [];
  }

  return [];
};
